import * as React from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Divider,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
    Snackbar,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Tooltip,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";

import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import BookingDates from "../bookings/components/BookingDates";
import BookingStatusChip from "../bookings/components/BookingStatusChip";
import {
    BookingInquiryDetails,
    DEFAULT_INQUIRY_STATUSES,
    safeStr,
    nightsBetween,
} from "./bookingInquiryTypes";

import { adminFetch } from "../../../services/adminFetch";

type ToastState = { open: boolean; msg: string; severity: "success" | "error" };

function canCopy(v?: string | null) {
    return !!(v && v.trim().length > 0);
}

async function copyToClipboard(text: string) {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        return false;
    }
}

async function readJsonOrText(res: Response): Promise<any> {
    const txt = await res.text();
    if (!txt) return {};
    try {
        return JSON.parse(txt);
    } catch {
        return { _raw: txt };
    }
}

export default function AdminBookingInquiryDetailsPage() {
    const { locale, id } = useParams<{ locale: string; id: string }>();
    const nav = useNavigate();
    const inquiryId = Number(id);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [converting, setConverting] = React.useState(false);
    const [err, setErr] = React.useState<string | null>(null);

    const [item, setItem] = React.useState<BookingInquiryDetails | null>(null);
    const [status, setStatus] = React.useState<string>("");
    const [internalNotes, setInternalNotes] = React.useState<string>("");

    const [toast, setToast] = React.useState<ToastState>({
        open: false,
        msg: "",
        severity: "success",
    });

    // Create customer modal
    const [custOpen, setCustOpen] = React.useState(false);
    const [custSaving, setCustSaving] = React.useState(false);
    const [custName, setCustName] = React.useState("");
    const [custEmail, setCustEmail] = React.useState("");
    const [custPhone, setCustPhone] = React.useState("");

    const load = React.useCallback(async () => {
        if (!Number.isFinite(inquiryId)) {
            setErr("Invalid inquiry id.");
            return;
        }
        setLoading(true);
        setErr(null);

        const url = `/api/admin/booking-inquiries/${inquiryId}`;
        try {
            const res = await adminFetch(url, { method: "GET" });
            const j = await readJsonOrText(res);
            if (!res.ok) throw new Error(j?.error || `Request failed (${res.status})`);

            const b = (j?.item ?? j) as BookingInquiryDetails;

            setItem(b || null);
            setStatus(safeStr((b as any)?.status, ""));
            setInternalNotes(safeStr((b as any)?.internal_notes, ""));

            setCustName(safeStr((b as any)?.full_name, ""));
            setCustEmail(safeStr((b as any)?.email, ""));
            setCustPhone(safeStr((b as any)?.phone, ""));
        } catch (e: any) {
            setErr(e?.message || "Failed to load inquiry.");
            setItem(null);
        } finally {
            setLoading(false);
        }
    }, [inquiryId]);

    React.useEffect(() => {
        load();
    }, [load]);

    const onSave = async () => {
        if (!item) return;
        setSaving(true);

        const url = `/api/admin/booking-inquiries/${inquiryId}`;
        try {
            const res = await adminFetch(url, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    status: status || null,
                    internal_notes: internalNotes || null,
                }),
            });

            const j = await readJsonOrText(res);
            if (!res.ok) throw new Error(j?.error || `Save failed (${res.status})`);

            const updated = (j?.item ?? j) as BookingInquiryDetails;
            setItem(updated || ({ ...item, status, internal_notes: internalNotes } as any));
            setToast({ open: true, msg: "Saved successfully.", severity: "success" });
        } catch (e: any) {
            setToast({ open: true, msg: e?.message || "Save failed.", severity: "error" });
        } finally {
            setSaving(false);
        }
    };

    const onConvertToBooking = async () => {
        if (!item) return;
        setConverting(true);

        const url = `/api/admin/bookings/from-inquiry/${inquiryId}`;
        try {
            const res = await adminFetch(url, { method: "POST" });
            const j = await readJsonOrText(res);
            if (!res.ok) throw new Error(j?.error || `Convert failed (${res.status})`);

            const bookingId = j?.booking?.id ?? j?.id ?? null;
            setToast({ open: true, msg: "Converted to booking successfully.", severity: "success" });

            if (bookingId) {
                nav(`/${locale || "el"}/admin/bookings/${bookingId}`);
                return;
            }
            await load();
        } catch (e: any) {
            setToast({ open: true, msg: e?.message || "Convert failed.", severity: "error" });
        } finally {
            setConverting(false);
        }
    };

    const openCustomerModal = () => {
        setCustName(safeStr((item as any)?.full_name, custName));
        setCustEmail(safeStr((item as any)?.email, custEmail));
        setCustPhone(safeStr((item as any)?.phone, custPhone));
        setCustOpen(true);
    };

    const onCreateCustomer = async () => {
        const name = custName.trim();
        if (!name) {
            setToast({ open: true, msg: "Customer name is required.", severity: "error" });
            return;
        }

        setCustSaving(true);
        try {
            const res = await adminFetch(`/api/admin/customers`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    full_name: name,
                    email: custEmail.trim() || null,
                    phone: custPhone.trim() || null,
                }),
            });

            const j = await readJsonOrText(res);
            if (!res.ok) throw new Error(j?.error || `Create customer failed (${res.status})`);

            setToast({ open: true, msg: "Customer created.", severity: "success" });
            setCustOpen(false);
        } catch (e: any) {
            setToast({ open: true, msg: e?.message || "Create customer failed.", severity: "error" });
        } finally {
            setCustSaving(false);
        }
    };

    const propertyTitle =
        safeStr((item as any)?.property_title, "") ||
        safeStr((item as any)?.property_slug, "") ||
        ((item as any)?.property_id ? `Property #${(item as any).property_id}` : "—");

    const slug = safeStr((item as any)?.property_slug, "") || null;
    const propertyUrl = safeStr((item as any)?.property_url, "") || null;

    const checkIn = (item as any)?.check_in ?? null;
    const checkOut = (item as any)?.check_out ?? null;
    const nights = nightsBetween(checkIn, checkOut);

    const fullName = safeStr((item as any)?.full_name, "") || "—";
    const email = safeStr((item as any)?.email, "") || "";
    const phone = safeStr((item as any)?.phone, "") || "";
    const guestsTotal = (item as any)?.guests_total ?? null;
    const pets = (item as any)?.pets ?? null;
    const arrivalTime = safeStr((item as any)?.arrival_time, "") || "";
    const message = safeStr((item as any)?.message, "") || "";

    const createdAt = safeStr((item as any)?.created_at, "");
    const updatedAt = safeStr((item as any)?.updated_at, "");

    const canConvert =
        !!(item && (item as any)?.property_id && checkIn && checkOut) && !converting && !loading;

    const viewPropertyLink = slug ? `/${locale || "el"}/properties/${slug}` : null;

    return (
        <Stack spacing={2}>
            <Paper
                elevation={0}
                variant="outlined"
                sx={{
                    borderRadius: 3,
                    borderColor: "divider",
                    overflow: "hidden",
                    background:
                        "radial-gradient(900px 500px at 15% -20%, rgba(25,118,210,0.10), transparent 60%), radial-gradient(900px 500px at 85% -10%, rgba(46,125,50,0.10), transparent 60%)",
                }}
            >
                {/* HEADER */}
                <Box sx={{ p: 2 }}>
                    <Stack spacing={1.25}>
                        <Stack spacing={0.25}>
                            <Typography variant="h6" sx={{ fontWeight: 950 }}>
                                Inquiry #{Number.isFinite(inquiryId) ? inquiryId : "—"}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word" }}>
                                {propertyTitle}
                            </Typography>

                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                <BookingStatusChip status={(item as any)?.status ?? null} />

                                {createdAt ? (
                                    <Chip size="small" variant="outlined" label={`Created: ${createdAt}`} sx={{ borderRadius: 999, fontWeight: 800 }} />
                                ) : null}
                                {updatedAt ? (
                                    <Chip size="small" variant="outlined" label={`Updated: ${updatedAt}`} sx={{ borderRadius: 999, fontWeight: 800 }} />
                                ) : null}
                                {nights !== null && nights >= 0 ? (
                                    <Chip size="small" variant="outlined" label={`${nights} night${nights === 1 ? "" : "s"}`} sx={{ borderRadius: 999, fontWeight: 800 }} />
                                ) : null}
                            </Stack>
                        </Stack>

                        {/* Actions: 2-column grid on mobile */}
                        <Box
                            sx={{
                                display: "grid",
                                gap: 1,
                                gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(5, auto)" },
                                justifyContent: { md: "flex-end" },
                                alignItems: "center",
                            }}
                        >
                            <Button
                                fullWidth={isMobile}
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => nav(-1)}
                                sx={{ borderRadius: 2, fontWeight: 900 }}
                            >
                                Back
                            </Button>

                            {viewPropertyLink ? (
                                <Button
                                    fullWidth={isMobile}
                                    component={RouterLink}
                                    to={viewPropertyLink}
                                    variant="outlined"
                                    startIcon={<OpenInNewIcon />}
                                    sx={{ borderRadius: 2, fontWeight: 900 }}
                                >
                                    View property
                                </Button>
                            ) : propertyUrl ? (
                                <Button
                                    fullWidth={isMobile}
                                    component="a"
                                    href={propertyUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    variant="outlined"
                                    startIcon={<OpenInNewIcon />}
                                    sx={{ borderRadius: 2, fontWeight: 900 }}
                                >
                                    Open link
                                </Button>
                            ) : (
                                <Box />
                            )}

                            <Button
                                fullWidth={isMobile}
                                variant="outlined"
                                startIcon={<PersonAddIcon />}
                                onClick={openCustomerModal}
                                sx={{ borderRadius: 2, fontWeight: 900 }}
                            >
                                Create customer
                            </Button>

                            <Button
                                fullWidth={isMobile}
                                variant="contained"
                                startIcon={converting ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                                disabled={!canConvert}
                                onClick={onConvertToBooking}
                                sx={{ borderRadius: 2, fontWeight: 950 }}
                            >
                                Convert
                            </Button>

                            <Button
                                fullWidth={isMobile}
                                variant="contained"
                                color="primary"
                                startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                                disabled={saving || loading || !item}
                                onClick={onSave}
                                sx={{ borderRadius: 2, fontWeight: 950 }}
                            >
                                Save
                            </Button>
                        </Box>
                    </Stack>
                </Box>

                <Divider />

                <Box sx={{ p: 2 }}>
                    {err ? <Alert severity="error">{err}</Alert> : null}

                    {loading ? (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
                            <CircularProgress size={18} />
                            <Typography variant="body2" color="text.secondary">
                                Loading…
                            </Typography>
                        </Stack>
                    ) : item ? (
                        <Stack spacing={2}>
                            {/* Stay details */}
                            <Paper variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
                                <Stack spacing={1}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>
                                        Stay details
                                    </Typography>
                                    <Divider />

                                    <Box
                                        sx={{
                                            display: "grid",
                                            gap: 2,
                                            gridTemplateColumns: { xs: "1fr 1fr", md: "2fr 1fr 1fr 1.2fr 1.4fr" },
                                            alignItems: "start",
                                        }}
                                    >
                                        <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Dates
                                            </Typography>
                                            <BookingDates checkIn={checkIn} checkOut={checkOut} />
                                        </Box>

                                        <Box>
                                            <Typography variant="body2" color="text.secondary">
                                                Guests
                                            </Typography>
                                            <Typography sx={{ fontWeight: 900 }}>{guestsTotal ?? "—"}</Typography>
                                        </Box>

                                        <Box>
                                            <Typography variant="body2" color="text.secondary">
                                                Pets
                                            </Typography>
                                            <Typography sx={{ fontWeight: 900 }}>
                                                {pets === true ? "Yes" : pets === false ? "No" : "—"}
                                            </Typography>
                                        </Box>

                                        <Box>
                                            <Typography variant="body2" color="text.secondary">
                                                Arrival
                                            </Typography>
                                            <Typography sx={{ fontWeight: 900 }}>{arrivalTime || "—"}</Typography>
                                        </Box>

                                        <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}>
                                            <TextField
                                                select
                                                label="Status"
                                                value={status}
                                                onChange={(e) => setStatus(e.target.value)}
                                                size="small"
                                                fullWidth
                                            >
                                                {DEFAULT_INQUIRY_STATUSES.filter((x) => x.value !== "").map((o) => (
                                                    <MenuItem key={o.value} value={o.value}>
                                                        {o.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Box>
                                    </Box>
                                </Stack>
                            </Paper>

                            {/* Customer info + notes */}
                            <Box
                                sx={{
                                    display: "grid",
                                    gap: 2,
                                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                                }}
                            >
                                <Paper variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
                                    <Stack spacing={1}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>
                                            Customer info
                                        </Typography>
                                        <Divider />

                                        <Typography sx={{ fontWeight: 900 }}>{fullName}</Typography>

                                        {/* Email */}
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">
                                                Email
                                            </Typography>
                                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                                <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                                                    {email || "—"}
                                                </Typography>

                                                <Tooltip title="Copy email">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            disabled={!canCopy(email)}
                                                            onClick={async () => {
                                                                const ok = await copyToClipboard(email);
                                                                setToast({
                                                                    open: true,
                                                                    msg: ok ? "Email copied." : "Could not copy.",
                                                                    severity: ok ? "success" : "error",
                                                                });
                                                            }}
                                                        >
                                                            <ContentCopyIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>

                                                {email ? (
                                                    <Tooltip title="Send email">
                                                        <IconButton size="small" component="a" href={`mailto:${email}`}>
                                                            <EmailIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                ) : null}
                                            </Stack>
                                        </Box>

                                        {/* Phone */}
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">
                                                Phone
                                            </Typography>
                                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                                <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                                                    {phone || "—"}
                                                </Typography>

                                                <Tooltip title="Copy phone">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            disabled={!canCopy(phone)}
                                                            onClick={async () => {
                                                                const ok = await copyToClipboard(phone);
                                                                setToast({
                                                                    open: true,
                                                                    msg: ok ? "Phone copied." : "Could not copy.",
                                                                    severity: ok ? "success" : "error",
                                                                });
                                                            }}
                                                        >
                                                            <ContentCopyIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>

                                                {phone ? (
                                                    <Tooltip title="Call">
                                                        <IconButton size="small" component="a" href={`tel:${phone}`}>
                                                            <PhoneIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                ) : null}
                                            </Stack>
                                        </Box>

                                        <Divider />

                                        <Typography variant="body2" color="text.secondary">
                                            Locale: {safeStr((item as any)?.locale, "—")} • IP: {safeStr((item as any)?.ip, "—")}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word" }}>
                                            User agent: {safeStr((item as any)?.user_agent, "—")}
                                        </Typography>
                                    </Stack>
                                </Paper>

                                <Paper variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
                                    <Stack spacing={1}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>
                                            Internal notes
                                        </Typography>
                                        <Divider />
                                        <TextField
                                            value={internalNotes}
                                            onChange={(e) => setInternalNotes(e.target.value)}
                                            multiline
                                            minRows={isMobile ? 5 : 8}
                                            placeholder="Add internal notes for this inquiry…"
                                            fullWidth
                                        />
                                        <Typography variant="caption" color="text.secondary">
                                            Only admins can see these notes.
                                        </Typography>
                                    </Stack>
                                </Paper>
                            </Box>

                            {/* Customer message */}
                            <Paper variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
                                <Stack spacing={1}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>
                                        Customer message
                                    </Typography>
                                    <Divider />
                                    {message ? (
                                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                            {message}
                                        </Typography>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            No message.
                                        </Typography>
                                    )}
                                </Stack>
                            </Paper>
                        </Stack>
                    ) : (
                        <Alert severity="info">No inquiry found.</Alert>
                    )}
                </Box>
            </Paper>

            {/* Create customer dialog */}
            <Dialog
                open={custOpen}
                onClose={() => setCustOpen(false)}
                fullWidth
                maxWidth="sm"
                fullScreen={isMobile}
            >
                <DialogTitle sx={{ fontWeight: 950 }}>Create customer</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Alert severity="info">
                            This creates a customer record. Later, when converting to booking, the system can match by email/phone.
                        </Alert>

                        <TextField label="Full name" value={custName} onChange={(e) => setCustName(e.target.value)} required autoFocus />
                        <TextField label="Email" value={custEmail} onChange={(e) => setCustEmail(e.target.value)} placeholder="optional" />
                        <TextField label="Phone" value={custPhone} onChange={(e) => setCustPhone(e.target.value)} placeholder="optional" />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCustOpen(false)} sx={{ fontWeight: 800 }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={onCreateCustomer}
                        disabled={custSaving}
                        startIcon={custSaving ? <CircularProgress size={16} /> : <PersonAddIcon />}
                        sx={{ borderRadius: 2, fontWeight: 900 }}
                    >
                        Create
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={toast.open}
                autoHideDuration={3500}
                onClose={() => setToast((t) => ({ ...t, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity={toast.severity}
                    variant="filled"
                    onClose={() => setToast((t) => ({ ...t, open: false }))}
                    sx={{ fontWeight: 800 }}
                >
                    {toast.msg}
                </Alert>
            </Snackbar>
        </Stack>
    );
}
